import React, { useState, useEffect, useMemo } from "react";
import { Table, Button, Dropdown, DropdownButton, Badge, Collapse, Card, Row, Col, Tooltip, OverlayTrigger } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  FaSearch,
  FaBoxes,
  FaChevronDown,
  FaChevronUp,
  FaEye,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaLayerGroup,
  FaCube,
  FaBoxOpen,
  FaBarcode,
  FaClipboard,
  FaFileInvoice,
  FaHistory,
  FaFilter,
  FaCheck,
  FaCompressAlt,
  FaExpandAlt,
  FaSlidersH,
  FaTag,
} from "react-icons/fa";
import { BiRefresh } from "react-icons/bi";

import StockDetails from "./StockDetails";
import StockFilters from "./StockFilters";
import { getAllItemsWithStock, getStockStatistics } from "../../redux/features/stock.features";
import { formatCurrency, truncateText } from "../../utility";

const BRAND_COLOR = "#001c3a";

/* -------------------------------------------------------------------------- */
/*                 1. HIGH-DENSITY KPI SEGMENTED RIBBON                      */
/* -------------------------------------------------------------------------- */
const StockKPI = ({ stats, onFilterClick, activeFilter, loading }) => {
  const segments = [
    { id: "all", label: "All Items", value: stats.totalItems || 0, dotColor: "#64748b", activeBg: "#f1f5f9" },
    { id: "in_stock", label: "In Stock", value: stats.inStock || 0, dotColor: "#10b981", activeBg: "#ecfdf5" },
    { id: "low_stock", label: "Low Stock", value: stats.lowStock || 0, dotColor: "#f59e0b", activeBg: "#fffbeb" },
    { id: "out_stock", label: "Sold Out", value: stats.outStock || 0, dotColor: "#ef4444", activeBg: "#fef2f2" },
    { id: "purchased", label: "Purchased", value: stats.purchased || 0, dotColor: "#0284c7", activeBg: "#f0f9ff" },
    { id: "never_purchased", label: "Never Purchased", value: stats.neverPurchased || 0, dotColor: "#8b5cf6", activeBg: "#f5f3ff" },
  ];

  return (
    <div
      className="bg-white border rounded-3 p-1 mb-3 d-flex align-items-center gap-1 shadow-sm overflow-x-auto w-100"
      style={{
        borderColor: "#e2e8f0",
        minHeight: "42px",
        scrollbarWidth: "none",
        WebkitOverflowScrolling: "touch",
        overscrollBehaviorX: "contain",
      }}
    >
      {segments.map((seg) => {
        const isActive = activeFilter === seg.id;
        return (
          <button
            key={seg.id}
            type="button"
            onClick={() => onFilterClick(seg.id)}
            className="btn btn-sm d-flex align-items-center gap-2 px-3 py-1 border-0 rounded-2 text-nowrap transition-all flex-shrink-0"
            style={{
              background: isActive ? seg.activeBg : "transparent",
              color: isActive ? "#0f172a" : "#64748b",
              fontWeight: isActive ? 600 : 500,
              fontSize: "0.78rem",
              boxShadow: isActive ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
              border: isActive ? "1px solid rgba(0,0,0,0.06)" : "1px solid transparent",
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                backgroundColor: seg.dotColor,
                display: "inline-block",
                boxShadow: isActive ? `0 0 0 2px ${seg.dotColor}25` : "none",
              }}
            />
            <span>{seg.label}</span>
            <span
              className="badge rounded-pill"
              style={{
                backgroundColor: isActive ? "#ffffff" : "#f1f5f9",
                color: isActive ? "#0f172a" : "#64748b",
                fontSize: "0.72rem",
                fontWeight: 700,
                border: "1px solid rgba(0,0,0,0.05)",
                padding: "2px 6px",
              }}
            >
              {loading ? "..." : seg.value.toLocaleString()}
            </span>
          </button>
        );
      })}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*              2. NESTED BREAKDOWN: SERIAL MATRIX & BATCH LEDGER            */
/* -------------------------------------------------------------------------- */
const VirtualizedSerialMatrix = ({ filteredList, handleCopy, copiedId }) => {
  const [displayCount, setDisplayCount] = useState(60);

  const visibleList = useMemo(() => {
    return filteredList.slice(0, displayCount);
  }, [filteredList, displayCount]);

  const hasMore = filteredList.length > displayCount;

  return (
    <div>
      <div
        className="d-flex flex-wrap gap-1.5 p-2 bg-white border rounded-3 shadow-xs"
        style={{
          maxHeight: "260px",
          overflowY: "auto",
        }}
      >
        {visibleList.map((s, idx) => {
          const id = s._id || s.serialNumber || idx;
          const isCopied = copiedId === id;
          const isDamaged = s.status === "Damaged";

          return (
            <div
              key={id}
              onClick={() => handleCopy(s.serialNumber, id)}
              className="d-flex align-items-center gap-1.5 px-2 py-1 bg-light border rounded-2 cursor-pointer transition-all hover-shadow"
              title={`Click to copy: ${s.serialNumber}${s.batch_number ? ` (Batch: ${s.batch_number})` : ""}${s.unitCost ? ` | Cost: ${formatCurrency(s.unitCost)}` : ""}`}
              style={{
                fontSize: "0.72rem",
                userSelect: "none",
                cursor: "pointer",
                borderColor: isCopied ? "#86efac" : isDamaged ? "#fca5a5" : "#e2e8f0",
                backgroundColor: isCopied ? "#f0fdf4" : isDamaged ? "#fef2f2" : "#f8fafc",
              }}
            >
              <FaBarcode size={10} className={isCopied ? "text-success" : "text-muted opacity-75"} />
              <span className="font-monospace fw-semibold text-dark">{s.serialNumber}</span>

              {s.condition && s.condition !== "Brand New" && (
                <span
                  className="badge bg-secondary bg-opacity-25 text-dark rounded-pill px-1"
                  style={{ fontSize: "0.62rem" }}
                >
                  {s.condition}
                </span>
              )}

              {s.status && s.status !== "Available" && (
                <span
                  className={`badge rounded-pill px-1 ${isDamaged ? "bg-danger text-white" : "bg-secondary text-white"}`}
                  style={{ fontSize: "0.62rem" }}
                >
                  {s.status}
                </span>
              )}

              {isCopied ? (
                <FaCheck size={9} className="text-success ms-1" />
              ) : (
                <FaClipboard size={9} className="text-muted opacity-50 ms-0.5" />
              )}
            </div>
          );
        })}
      </div>

      {hasMore && (
        <div className="d-flex justify-content-between align-items-center mt-2 px-1">
          <small className="text-muted" style={{ fontSize: "0.72rem" }}>
            Showing {visibleList.length} of {filteredList.length} serials
          </small>
          <Button
            variant="link"
            size="sm"
            className="p-0 text-primary fw-semibold"
            style={{ fontSize: "0.75rem", textDecoration: "none" }}
            onClick={() => setDisplayCount((prev) => prev + 60)}
          >
            Load 60 more ({filteredList.length - visibleList.length} remaining)
          </Button>
        </div>
      )}
    </div>
  );
};

const InventoryBreakdown = ({ isExpanded, item, isTableMode = true }) => {
  const [copiedId, setCopiedId] = useState(null);
  const [nestedSearch, setNestedSearch] = useState("");

  const isSerialized = item.isSerialized === true || item.serialized === true;
  const rawList = isSerialized ? item.serializedItems || [] : item.batches || [];

  const filteredList = useMemo(() => {
    if (!nestedSearch.trim()) return rawList;
    const q = nestedSearch.toLowerCase();
    if (isSerialized) {
      return rawList.filter((s) => (s.serialNumber || "").toLowerCase().includes(q));
    }
    return rawList.filter(
      (b) =>
        (b.batch_number || "").toLowerCase().includes(q) ||
        (b.purchase?.referenceNumber || "").toLowerCase().includes(q) ||
        (b.purchase?.supplier_bill_no || "").toLowerCase().includes(q)
    );
  }, [rawList, nestedSearch, isSerialized]);

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const content = (
    <div
      className="p-2.5 p-md-3 border-bottom"
      style={{
        backgroundColor: "#f8fafc",
        boxShadow: "inset 0 2px 4px rgba(0,0,0,0.03)",
      }}
    >
      {/* Inner Header Bar */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
        <div className="d-flex align-items-center gap-2">
          <span className="badge bg-dark bg-opacity-75 text-white font-monospace" style={{ fontSize: "0.68rem" }}>
            {isSerialized ? "SERIAL MATRIX" : "BATCH LEDGER"}
          </span>
          <span className="text-muted small" style={{ fontSize: "0.74rem" }}>
            {filteredList.length} of {rawList.length} records
          </span>
        </div>

        {/* In-row quick search */}
        {rawList.length > 5 && (
          <div style={{ maxWidth: "200px" }}>
            <input
              type="text"
              className="form-control form-control-sm bg-white shadow-none font-monospace"
              placeholder={`Filter ${isSerialized ? "serials" : "batches"}...`}
              style={{ fontSize: "0.72rem", height: "26px" }}
              value={nestedSearch}
              onChange={(e) => setNestedSearch(e.target.value)}
            />
          </div>
        )}
      </div>

      {/* Content: Serial Chip Matrix vs Batch Table */}
      {filteredList.length > 0 ? (
        isSerialized ? (
          <VirtualizedSerialMatrix filteredList={filteredList} handleCopy={handleCopy} copiedId={copiedId} />
        ) : (
          <div className="bg-white border rounded-2 overflow-hidden shadow-xs">
            <Table size="sm" hover className="mb-0 align-middle text-nowrap" style={{ fontSize: "0.76rem" }}>
              <thead className="bg-light text-muted fw-bold" style={{ fontSize: "0.68rem", textTransform: "uppercase" }}>
                <tr>
                  <th className="ps-3 py-1">Batch Code</th>
                  <th className="py-1">Purchase / Supplier Ref</th>
                  <th className="py-1 text-center">Available Stock</th>
                  <th className="py-1 text-end pe-3">Unit Cost</th>
                </tr>
              </thead>
              <tbody>
                {filteredList.map((b, idx) => {
                  const supplierRef = b.purchase?.referenceNumber || b.purchase?.supplier_bill_no || "N/A";
                  const purchaseDate = b.purchaseDate ? new Date(b.purchaseDate).toLocaleDateString() : "";

                  return (
                    <tr key={b._id || b.batch_number || idx}>
                      <td className="ps-3 py-1 fw-bold font-monospace text-dark">
                        <FaBoxOpen className="text-primary me-1 opacity-75" size={11} />
                        {b.batch_number}
                      </td>
                      <td className="py-1 text-muted">
                        <span className="fw-semibold text-dark">{supplierRef}</span>
                        {purchaseDate && <span className="ms-2 opacity-75" style={{ fontSize: "0.68rem" }}>({purchaseDate})</span>}
                      </td>
                      <td className="py-1 text-center">
                        <span className="badge bg-success bg-opacity-10 text-success fw-bold px-2 py-0.5 rounded-pill font-monospace">
                          {b.availableQty} units
                        </span>
                      </td>
                      <td className="py-1 text-end pe-3 font-monospace fw-semibold text-dark">
                        {formatCurrency(b.unitCost)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )
      ) : (
        <div className="text-center py-2 text-muted" style={{ fontSize: "0.76rem" }}>
          No matching records found.
        </div>
      )}
    </div>
  );

  if (!isTableMode) {
    return <Collapse in={isExpanded}>{content}</Collapse>;
  }

  return (
    <Collapse in={isExpanded}>
      <tr>
        <td colSpan="100%" className="p-0 border-0">
          {content}
        </td>
      </tr>
    </Collapse>
  );
};

/* -------------------------------------------------------------------------- */
/*                            MAIN STOCK DASHBOARD                            */
/* -------------------------------------------------------------------------- */
const StockDashboard = ({ propSearch, hideHeader }) => {
  const dispatch = useDispatch();
  const { stocks, status, statistics, pagination } = useSelector((state) => state.stockReducer);
  const isLoading = status === "loading";

  // Density mode state: 'compact' (32px rows) or 'comfortable' (44px rows)
  const [density, setDensity] = useState("compact");

  const [searchTerm, setSearchTerm] = useState(propSearch || "");
  const [statusFilter, setStatusFilter] = useState("all");
  const [advancedFilters, setAdvancedFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(propSearch || "");
  const [isDebouncing, setIsDebouncing] = useState(false);

  // Sync propSearch from parent component
  useEffect(() => {
    if (propSearch !== undefined && propSearch !== searchTerm) {
      setSearchTerm(propSearch);
      setDebouncedSearchTerm(propSearch);
      setPage(1);
    }
  }, [propSearch]);

  // Debounce search keystrokes
  useEffect(() => {
    setIsDebouncing(true);
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1);
      setIsDebouncing(false);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  /* -------------------------- DATA FETCH WITH ABORT ------------------------- */
  const fetchStock = (immediateSearch) => {
    const searchVal = immediateSearch !== undefined ? immediateSearch : debouncedSearchTerm;
    return dispatch(
      getAllItemsWithStock({
        page,
        limit: itemsPerPage,
        search: searchVal || undefined,
        stockStatus: statusFilter !== "all" ? statusFilter : undefined,
        ...advancedFilters,
      })
    );
  };

  useEffect(() => {
    const promise = fetchStock();
    dispatch(getStockStatistics());

    return () => {
      promise.abort();
    };
  }, [dispatch, page, itemsPerPage, statusFilter, debouncedSearchTerm, advancedFilters]);

  const handleImmediateSearch = (term) => {
    setIsDebouncing(false);
    setPage(1);
    setDebouncedSearchTerm(term);
  };

  const handleKPIFilter = (filterKey) => {
    setStatusFilter(filterKey);
    setPage(1);
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setIsDebouncing(false);
  };

  const toggleRow = (id) => setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));

  const activeFiltersCount = Object.keys(advancedFilters).length;
  const showLoadingIndicator = isLoading || isDebouncing;
  const totalPages = pagination ? Math.ceil(pagination.total / itemsPerPage) : 1;

  return (
    <div className={hideHeader ? "container-fluid p-0" : "container-xxl flex-grow-1 container-p-y pt-2 px-2 px-md-3"}>
      {/* 1. SLIM COMMAND HEADER */}
      {!hideHeader && (
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 pb-1 gap-2">
          <div className="d-flex align-items-center gap-2">
            <h5 className="fw-bold mb-0 d-flex align-items-center gap-2" style={{ color: BRAND_COLOR, fontSize: "1.1rem" }}>
              <FaCube className="text-primary" size={18} /> Stock Overview
            </h5>
            <span className="badge rounded-pill bg-light text-secondary border font-monospace" style={{ fontSize: "0.72rem" }}>
              {pagination?.total ? `${pagination.total.toLocaleString()} SKUs` : "Live Catalog"}
            </span>
          </div>

          <div className="d-flex align-items-center gap-2">
            {/* Density Toggle (Desktop Only) */}
            <div className="btn-group btn-group-sm bg-white border rounded-2 shadow-xs p-0.5 d-none d-md-inline-flex">
              <button
                type="button"
                className={`btn btn-sm px-2 py-0.5 border-0 rounded-1 ${density === "compact" ? "bg-primary text-white" : "text-muted"}`}
                onClick={() => setDensity("compact")}
                title="Compact density"
                style={{ fontSize: "0.72rem" }}
              >
                <FaCompressAlt size={10} className="me-1" /> Compact
              </button>
              <button
                type="button"
                className={`btn btn-sm px-2 py-0.5 border-0 rounded-1 ${density === "comfortable" ? "bg-primary text-white" : "text-muted"}`}
                onClick={() => setDensity("comfortable")}
                title="Comfortable density"
                style={{ fontSize: "0.72rem" }}
              >
                <FaExpandAlt size={10} className="me-1" /> Normal
              </button>
            </div>

            {/* Refresh */}
            <Button
              variant="light"
              size="sm"
              className="border shadow-xs d-flex align-items-center gap-1 py-1 px-2.5 text-dark bg-white rounded-2 fw-semibold"
              onClick={() => fetchStock()}
              title="Refresh stock levels"
              style={{ fontSize: "0.75rem" }}
            >
              <BiRefresh size={16} className={showLoadingIndicator ? "spin text-primary" : "text-primary"} />
              <span>Sync</span>
            </Button>
          </div>
        </div>
      )}

      {/* 2. HIGH-DENSITY KPI RIBBON */}
      <StockKPI
        stats={statistics || {}}
        onFilterClick={handleKPIFilter}
        activeFilter={statusFilter}
        loading={isLoading}
      />

      {/* 3. UNIFIED COMMAND SEARCH & FILTER BAR */}
      <div
        className="bg-white border rounded-3 p-1.5 mb-3 shadow-sm position-relative"
        style={{ borderColor: "#e2e8f0" }}
      >
        <div className="d-flex align-items-center gap-2">
          {/* Search Icon */}
          <FaSearch className="text-muted ms-2 flex-shrink-0" size={13} />

          {/* Realtime Search Input */}
          <input
            type="text"
            className="form-control form-control-sm border-0 shadow-none bg-transparent font-monospace"
            placeholder="SEARCH ITEMS, SKU, BARCODE, SERIAL, VAR..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ fontSize: "0.8rem", height: "30px" }}
            autoComplete="off"
            id="stock-search-input"
          />

          {/* Typing / Searching Badge */}
          <div style={{ flexShrink: 0 }}>
            {isDebouncing ? (
              <span className="badge bg-primary bg-opacity-10 text-primary font-monospace" style={{ fontSize: "0.68rem" }}>
                Typing...
              </span>
            ) : isLoading ? (
              <span className="badge bg-primary bg-opacity-10 text-primary font-monospace" style={{ fontSize: "0.68rem" }}>
                Searching...
              </span>
            ) : debouncedSearchTerm ? (
              <span className="badge bg-light text-secondary border font-monospace" style={{ fontSize: "0.68rem" }}>
                {pagination?.total ?? stocks.length} matches
              </span>
            ) : null}
          </div>

          {/* Clear Search */}
          {searchTerm && !showLoadingIndicator && (
            <button
              type="button"
              className="btn btn-link p-0 text-muted hover-text-dark"
              onClick={() => {
                setSearchTerm("");
                handleImmediateSearch("");
              }}
              title="Clear search"
              style={{ fontSize: "1.1rem", lineHeight: 1 }}
            >
              ×
            </button>
          )}

          {/* Advanced Filter Toggle Pill */}
          <button
            type="button"
            className={`btn btn-sm d-flex align-items-center gap-1.5 px-2.5 py-1 rounded-2 border ${
              showFilters || activeFiltersCount > 0 ? "btn-primary text-white" : "btn-light text-secondary"
            }`}
            onClick={() => setShowFilters(!showFilters)}
            style={{ fontSize: "0.75rem", flexShrink: 0 }}
          >
            <FaFilter size={10} />
            <span>Filters</span>
            {activeFiltersCount > 0 && (
              <span className="badge bg-white text-dark rounded-pill ms-1" style={{ fontSize: "0.65rem" }}>
                {activeFiltersCount}
              </span>
            )}
          </button>
        </div>

        {/* Shimmer line when searching */}
        {showLoadingIndicator && (
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 6,
              right: 6,
              height: "2px",
              background: "linear-gradient(90deg, #6366f1, #818cf8, #6366f1)",
              backgroundSize: "200% 100%",
              animation: "shimmer 1.2s linear infinite",
              borderRadius: "0 0 6px 6px",
            }}
          />
        )}
      </div>

      {/* 4. ADVANCED FILTER POPUP PANEL */}
      <StockFilters
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        onFilterChange={(filters) => {
          setAdvancedFilters(filters);
          setPage(1);
        }}
        activeFiltersCount={activeFiltersCount}
      />

      {/* 5. INVENTORY CONTENT (Responsive Table for Desktop + Responsive Cards for Mobile) */}
      <Card className="border-0 shadow-sm overflow-hidden bg-white mb-3" style={{ borderRadius: "8px" }}>
        {/* DESKTOP TABLE VIEW */}
        <div className="table-responsive d-none d-md-block">
          <Table
            hover
            className={`align-middle mb-0 text-nowrap ${density === "compact" ? "table-sm" : ""}`}
            style={{ fontSize: density === "compact" ? "0.8rem" : "0.875rem" }}
          >
            <thead className="bg-light text-muted fw-semibold" style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              <tr>
                <th style={{ width: "32px" }}></th>
                <th style={{ width: "40px" }}></th>
                <th>Item & Variant Details</th>
                <th className="text-center">Category</th>
                <th className="text-center" style={{ width: "130px" }}>Stock Status</th>
                <th className="text-end">Cost</th>
                <th className="text-end">Total Value</th>
                <th className="text-center" style={{ width: "90px" }}>State</th>
                <th className="text-end pe-3" style={{ width: "70px" }}>Action</th>
              </tr>
            </thead>

            <tbody>
              {/* Skeleton Rows on search */}
              {showLoadingIndicator && (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={`skel-${i}`} style={{ opacity: 0.5 }}>
                    <td><div className="bg-light rounded" style={{ width: 14, height: 14 }} /></td>
                    <td><div className="bg-light rounded" style={{ width: 28, height: 28 }} /></td>
                    <td>
                      <div className="bg-light rounded mb-1" style={{ width: "50%", height: 12 }} />
                      <div className="bg-light rounded" style={{ width: "25%", height: 9 }} />
                    </td>
                    <td><div className="bg-light rounded mx-auto" style={{ width: 50, height: 16 }} /></td>
                    <td><div className="bg-light rounded mx-auto" style={{ width: 70, height: 10 }} /></td>
                    <td><div className="bg-light rounded ms-auto" style={{ width: 45, height: 12 }} /></td>
                    <td><div className="bg-light rounded ms-auto" style={{ width: 55, height: 12 }} /></td>
                    <td><div className="bg-light rounded mx-auto" style={{ width: 55, height: 16 }} /></td>
                    <td><div className="bg-light rounded ms-auto" style={{ width: 22, height: 22 }} /></td>
                  </tr>
                ))
              )}

              {/* Empty state */}
              {!showLoadingIndicator && stocks.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-5 text-muted">
                    <FaBoxes size={32} className="mb-2 opacity-25" />
                    <div className="fw-semibold">No stock items match your criteria.</div>
                    <small className="text-muted">Try clearing the search query or adjusting your filters.</small>
                  </td>
                </tr>
              ) : (
                !showLoadingIndicator &&
                stocks.map((item) => {
                  const isLow = item.totalStock > 0 && item.totalStock <= (item.alertQuantity || 0);
                  const isOut = item.totalStock <= 0;
                  const isExpanded = expandedRows[item.uId];
                  const hasNested = item.isSerialized || (item.batches && item.batches.length > 0);

                  return (
                    <React.Fragment key={item.uId}>
                      <tr
                        className={`transition-all ${isLow ? "table-warning bg-opacity-25" : ""} ${isOut ? "opacity-80" : ""}`}
                        style={{
                          height: density === "compact" ? "36px" : "48px",
                          borderBottom: isExpanded ? "none" : "1px solid #f1f5f9",
                        }}
                      >
                        {/* Expand toggle */}
                        <td className="ps-2 text-center">
                          {hasNested && (
                            <button
                              type="button"
                              className="btn btn-link p-0 text-muted hover-text-primary"
                              onClick={() => toggleRow(item.uId)}
                              title={isExpanded ? "Collapse details" : "Expand breakdown"}
                              style={{ lineHeight: 1 }}
                            >
                              {isExpanded ? <FaChevronUp size={9} /> : <FaChevronDown size={9} />}
                            </button>
                          )}
                        </td>

                        {/* Thumbnail / Monogram */}
                        <td className="p-1 text-center">
                          <div
                            className="rounded-2 border d-flex align-items-center justify-content-center overflow-hidden bg-light"
                            style={{ width: density === "compact" ? 28 : 34, height: density === "compact" ? 28 : 34 }}
                          >
                            {item.itemImage ? (
                              <img src={item.itemImage} alt="" className="w-100 h-100 object-fit-cover" />
                            ) : (
                              <FaBoxOpen className="text-muted opacity-50" size={13} />
                            )}
                          </div>
                        </td>

                        {/* Composite Item & Variant Cell */}
                        <td>
                          <div className="d-flex flex-column">
                            <div className="d-flex align-items-center gap-1.5">
                              <span className="fw-bold text-dark text-truncate" style={{ maxWidth: "340px" }} title={item.itemName}>
                                {item.itemName}
                              </span>
                              {item.isVariant && (
                                <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25 font-monospace" style={{ fontSize: "0.65rem", padding: "1px 4px" }}>
                                  Variant
                                </span>
                              )}
                              {item.isSerialized && (
                                <span className="badge bg-dark bg-opacity-10 text-dark border border-dark border-opacity-25 font-monospace" style={{ fontSize: "0.65rem", padding: "1px 4px" }}>
                                  IMEI
                                </span>
                              )}
                            </div>
                            <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: "0.72rem" }}>
                              <span className="font-monospace">{item.barcode}</span>
                              {item.variantName && <span>• {item.variantName}</span>}
                            </div>
                          </div>
                        </td>

                        {/* Category */}
                        <td className="text-center">
                          <span className="badge bg-light text-secondary border font-monospace" style={{ fontSize: "0.7rem", fontWeight: 500 }}>
                            {item.category || "General"}
                          </span>
                        </td>

                        {/* Stock Progress Micro-Gauge */}
                        <td className="text-center px-3">
                          <div className="d-flex align-items-center justify-content-center gap-1 font-monospace fw-bold" style={{ fontSize: "0.78rem" }}>
                            <span className={isOut ? "text-danger" : isLow ? "text-warning" : "text-success"}>
                              {item.totalStock}
                            </span>
                            <span className="text-muted opacity-75 font-normal" style={{ fontSize: "0.7rem" }}>
                              / {item.alertQuantity || 0}
                            </span>
                          </div>
                          <div
                            className="bg-light rounded-pill overflow-hidden mt-0.5 mx-auto"
                            style={{ height: "3px", width: "70px" }}
                          >
                            <div
                              style={{
                                width: `${Math.min((item.totalStock / Math.max(item.alertQuantity * 2 || 1, 1)) * 100, 100)}%`,
                                height: "100%",
                                backgroundColor: isOut ? "#ef4444" : isLow ? "#f59e0b" : "#10b981",
                              }}
                            />
                          </div>
                        </td>

                        {/* Unit Cost */}
                        <td className="text-end font-monospace text-muted" style={{ fontVariantNumeric: "tabular-nums" }}>
                          {formatCurrency(item.lastUnitCost)}
                        </td>

                        {/* Valuation */}
                        <td className="text-end font-monospace fw-bold text-dark" style={{ fontVariantNumeric: "tabular-nums" }}>
                          {formatCurrency(item.totalStock * item.lastUnitCost)}
                        </td>

                        {/* State Pill */}
                        <td className="text-center">
                          <span
                            className="badge rounded-pill border font-monospace"
                            style={{
                              fontSize: "0.68rem",
                              backgroundColor: isOut ? "#fef2f2" : isLow ? "#fffbeb" : "#ecfdf5",
                              color: isOut ? "#dc2626" : isLow ? "#d97706" : "#059669",
                              borderColor: isOut ? "#fecaca" : isLow ? "#fde68a" : "#a7f3d0",
                            }}
                          >
                            {isOut ? "Sold Out" : isLow ? "Low Stock" : "In Stock"}
                          </span>
                        </td>

                        {/* Action */}
                        <td className="text-end pe-3">
                          <button
                            type="button"
                            className="btn btn-light btn-sm rounded-circle p-1 hover-shadow"
                            style={{ width: 26, height: 26, lineHeight: 1 }}
                            onClick={() => {
                              setSelectedItem(item);
                              setSelectedVariantId(item.variant_id);
                            }}
                            title="Inspect item history & batches"
                          >
                            <FaEye className="text-primary" size={11} />
                          </button>
                        </td>
                      </tr>

                      {/* Nested Serial/Batch Breakdown */}
                      <InventoryBreakdown isExpanded={isExpanded} item={item} isTableMode={true} />
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>

        {/* MOBILE HIGH-DENSITY CARD LIST VIEW */}
        <div className="d-block d-md-none p-2 d-flex flex-column gap-2 bg-light">
          {/* Skeleton on search */}
          {showLoadingIndicator && (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={`mob-skel-${i}`} className="p-3 bg-white rounded-3 border shadow-xs">
                <div className="bg-light rounded mb-2" style={{ width: "70%", height: 14 }} />
                <div className="bg-light rounded mb-2" style={{ width: "40%", height: 10 }} />
                <div className="bg-light rounded" style={{ width: "100%", height: 30 }} />
              </div>
            ))
          )}

          {/* Empty state */}
          {!showLoadingIndicator && stocks.length === 0 && (
            <div className="text-center py-5 bg-white rounded-3 border text-muted">
              <FaBoxes size={32} className="mb-2 opacity-25" />
              <div className="fw-semibold small">No stock items found</div>
            </div>
          )}

          {/* Mobile Item Cards */}
          {!showLoadingIndicator &&
            stocks.map((item) => {
              const isLow = item.totalStock > 0 && item.totalStock <= (item.alertQuantity || 0);
              const isOut = item.totalStock <= 0;
              const isExpanded = expandedRows[item.uId];
              const hasNested = item.isSerialized || (item.batches && item.batches.length > 0);
              const totalValuation = item.totalStock * item.lastUnitCost;

              return (
                <div
                  key={`mob-${item.uId}`}
                  className="bg-white rounded-3 border shadow-xs overflow-hidden"
                  style={{
                    borderLeft: `4px solid ${isOut ? "#ef4444" : isLow ? "#f59e0b" : "#10b981"}`,
                  }}
                >
                  <div className="p-2.5">
                    {/* Top Row: Title, Badges & State */}
                    <div className="d-flex align-items-start justify-content-between gap-2 mb-1.5">
                      <div className="min-w-0">
                        <div className="fw-bold text-dark text-truncate" style={{ fontSize: "0.85rem" }}>
                          {item.itemName}
                        </div>
                        <div className="d-flex align-items-center gap-1.5 flex-wrap text-muted small mt-0.5" style={{ fontSize: "0.7rem" }}>
                          <span className="font-monospace text-primary">{item.barcode}</span>
                          {item.variantName && <span>• {item.variantName}</span>}
                          <span className="badge bg-light text-secondary border font-monospace py-0 px-1">
                            {item.category || "General"}
                          </span>
                        </div>
                      </div>

                      {/* State Pill */}
                      <span
                        className="badge rounded-pill border font-monospace flex-shrink-0"
                        style={{
                          fontSize: "0.64rem",
                          backgroundColor: isOut ? "#fef2f2" : isLow ? "#fffbeb" : "#ecfdf5",
                          color: isOut ? "#dc2626" : isLow ? "#d97706" : "#059669",
                          borderColor: isOut ? "#fecaca" : isLow ? "#fde68a" : "#a7f3d0",
                        }}
                      >
                        {isOut ? "Sold Out" : isLow ? "Low Stock" : "In Stock"}
                      </span>
                    </div>

                    {/* Metrics Grid */}
                    <div className="p-2 rounded-2 bg-light border mb-2">
                      <Row className="g-2 text-center">
                        <Col xs={4}>
                          <div className="text-muted" style={{ fontSize: "0.62rem" }}>AVAILABLE</div>
                          <div className={`fw-bold font-monospace ${isOut ? "text-danger" : isLow ? "text-warning" : "text-success"}`} style={{ fontSize: "0.82rem" }}>
                            {item.totalStock} <span className="text-muted fw-normal small">/ {item.alertQuantity || 0}</span>
                          </div>
                        </Col>
                        <Col xs={4} className="border-start border-end">
                          <div className="text-muted" style={{ fontSize: "0.62rem" }}>UNIT COST</div>
                          <div className="fw-bold font-monospace text-dark text-truncate" style={{ fontSize: "0.8rem" }}>
                            {formatCurrency(item.lastUnitCost)}
                          </div>
                        </Col>
                        <Col xs={4}>
                          <div className="text-muted" style={{ fontSize: "0.62rem" }}>TOTAL VALUE</div>
                          <div className="fw-bold font-monospace text-primary text-truncate" style={{ fontSize: "0.8rem" }}>
                            {formatCurrency(totalValuation)}
                          </div>
                        </Col>
                      </Row>
                    </div>

                    {/* Action Bar */}
                    <div className="d-flex align-items-center justify-content-between gap-2 pt-1 border-top">
                      {hasNested ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0 text-decoration-none text-muted d-flex align-items-center gap-1 font-monospace"
                          style={{ fontSize: "0.72rem" }}
                          onClick={() => toggleRow(item.uId)}
                        >
                          <span>{isExpanded ? "Hide Details" : "Batches / IMEI"}</span>
                          {isExpanded ? <FaChevronUp size={9} /> : <FaChevronDown size={9} />}
                        </button>
                      ) : (
                        <span className="text-muted small" style={{ fontSize: "0.68rem" }}>Standard SKU</span>
                      )}

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary rounded-pill py-0.5 px-2.5 d-flex align-items-center gap-1 font-monospace shadow-xs"
                        style={{ fontSize: "0.72rem" }}
                        onClick={() => {
                          setSelectedItem(item);
                          setSelectedVariantId(item.variant_id);
                        }}
                      >
                        <FaEye size={10} /> Inspect
                      </button>
                    </div>
                  </div>

                  {/* Mobile Collapsible Inventory Breakdown */}
                  <InventoryBreakdown isExpanded={isExpanded} item={item} isTableMode={false} />
                </div>
              );
            })}
        </div>

        {/* 6. COMPACT PAGINATION FOOTER */}
        <div className="card-footer bg-white border-top py-2 px-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div className="text-muted" style={{ fontSize: "0.75rem" }}>
            Showing {pagination?.total > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0}–
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
            <span className="fw-bold text-dark">{pagination?.total || 0}</span> items
          </div>

          <div className="d-flex align-items-center gap-2">
            {/* Page size selector */}
            <DropdownButton
              size="sm"
              variant="light"
              className="border rounded-2 shadow-xs font-monospace"
              title={`${pagination?.limit || 15} rows`}
              style={{ fontSize: "0.72rem" }}
            >
              {[15, 30, 50, 100].map((size) => (
                <Dropdown.Item
                  key={size}
                  onClick={() => {
                    setItemsPerPage(size);
                    setPage(1);
                  }}
                  style={{ fontSize: "0.75rem" }}
                >
                  {size} rows per page
                </Dropdown.Item>
              ))}
            </DropdownButton>

            {/* Prev / Next Buttons */}
            <div className="btn-group btn-group-sm">
              <Button
                variant="light"
                className="border font-monospace"
                disabled={pagination?.page <= 1}
                onClick={() => setPage((p) => p - 1)}
                style={{ fontSize: "0.72rem" }}
              >
                Previous
              </Button>
              <Button
                variant="light"
                className="border font-monospace"
                disabled={pagination?.page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                style={{ fontSize: "0.72rem" }}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* 7. SLIDE-OVER STOCK DETAILS DRAWER */}
      {selectedItem && (
        <StockDetails
          itemId={selectedItem._id}
          variantId={selectedVariantId}
          onClose={() => {
            setSelectedItem(null);
            setSelectedVariantId(null);
          }}
        />
      )}

      <style>{`
        .shadow-xs { box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.04); }
        .hover-shadow:hover { box-shadow: 0 2px 5px rgba(0,0,0,0.1); }
        .hover-text-primary:hover { color: #0284c7 !important; }
        .hover-text-dark:hover { color: #0f172a !important; }
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default StockDashboard;
